/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AccountsApiService } from '../../../../http';
import { BaseComponent } from '../../../../../../../core/components';
import { Component } from '@angular/core';
import { ErrorResponseModel } from '../../../../../../../core/models';
import { LogService, UserService } from '../../../../../../../core/services';
import { OwlOptions } from 'ngx-owl-carousel-o';
import { WidgetModel } from '../../../../models';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-advert-widget',
    templateUrl: './advert-widget.component.html',
    styleUrls: ['../widgetBaseComponent/widget.component.scss', './advert-widget.component.scss'],
})

export class AdvertWidgetComponent extends BaseComponent {
    /**
     * Carousel options.
     */
    public customOptions: OwlOptions = {
        center: true,
        dots: true,
        items: 1,
        loop: true,
        autoplaySpeed: 250,
        autoplay: true,
        responsive: {
            0: {
                items: 1,
            },
        }
    };

    /**
     * Slides to display.
     */
    public slides: any[] = [];

    /**
     * Boolean to show/hide calendly.
     */
    public hideCalendly: boolean = true;

    /**
     * User object collected from hubspot and passed to calendly.
     */
    public userObject: {
        name: string,
        email: string
    };

    constructor(
        private accountsApiService: AccountsApiService,
        private logService: LogService,
        private userService: UserService
    ) {
        super();

        this.slides.push(this.get30DayAdvert(), this.getBoostAdvert(), this.getScheduleAdvert());
        this.setUpUserDetails();
    }

    /**
     * Hide the calendly widget.
     */
    public hideCalendlyWidget(): void {
        this.hideCalendly = true;
    }

    /**
     * Show the calendly widget.
     * @private
     */
    private loadCalendly(): void {
        this.hideCalendly = false;
    }

    /**
     * Get trial advert details.
     * @private
     */
    private get30DayAdvert(): WidgetModel {
        const type: string = 'advertBlock';
        const title: string = 'dashboard_advert_trial_title';
        const details: string = `
            <li>A fully-featured virtual ADC deployed in minutes</li>
            <li>No bandwidth/feature restrictions</li>
            <li>Advanced features such as WAF and GSLB</li>
            `;
        const mainImageSrc: string = 'assets/centralisedManagement/dashboard-carousel-images/enterprise.png';
        const linkText: string = 'free_download';
        const linkUrl: string = '/cm/resources/platform/lbShuttleEnterprise';

        return { type, title, mainImageSrc, details, linkText, linkUrl };
    }

    /**
     * Get boost advert details.
     * @private
     */
    private getBoostAdvert(): WidgetModel {
        const type: string = 'advertBlock';
        const title: string = 'dashboard_advert_boost_title';
        const details: string = `
            <li>Unlimited ADC connections with unlimited ADC backups</li>
            <li>Two-factor authentication and single sign on (G Suite & Azure AD)</li>
            <li>Audit log, SAML and so much more people!</li>
            `;
        const mainImageSrc: string = 'assets/centralisedManagement/dashboard-carousel-images/spaceship.png';
        const linkText: string = 'upgrade_options';
        const linkUrl: string = '/cm/organisation/subscriptions';

        return { type, title, mainImageSrc, details, linkText, linkUrl };
    }

    /**
     * Get demo advert details.
     * @private
     */
    private getScheduleAdvert(): WidgetModel {
        const type: string = 'advertBlock';
        const title: string = 'dashboard_advert_demo_title';
        const details: string = `
            <li>Get help with setting up Shuttle</li>
            <li>Learn how to add an ADC</li>
            <li>Create schedules to back up or update your ADC</li>
            `;

        const mainImageSrc: string = 'assets/centralisedManagement/dashboard-carousel-images/demo.png';
        const linkText: string = 'subscriptions_book_a_demo';
        const callBack = this.loadCalendly.bind(this);

        return { type, title, mainImageSrc, details, linkText, callBack };
    }

    /**
     * Set up user details.
     * @private
     */
    private setUpUserDetails(): void {
        this.accountsApiService.get(this.userService.getUser('id'))
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (data: { firstName: string, lastName: string, email: string }): void => {
                    this.userObject = {
                        name: `${data.firstName} ${data.lastName}`,
                        email: data.email
                    };
                },
                error: (error: ErrorResponseModel): void => {
                    this.userObject = {
                        name: undefined,
                        email: undefined
                    };
                    this.logService.error('ActivateAccountComponent:activateAccount', error.message);
                }
            });
    }
}
